const PI_4 = Math.PI / 4;

export interface ImageMoments {
    raw00: number;
    rawX0: number;
    raw0Y: number;
    rawXY: number;
    rawXX: number;
    rawYY: number;

    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

export function newImageMoments(): ImageMoments {
    return {
        raw00: 0,
        rawX0: 0,
        raw0Y: 0,
        rawXY: 0,
        rawXX: 0,
        rawYY: 0,
        minX: Number.MAX_SAFE_INTEGER,
        maxX: 0,
        minY: Number.MAX_SAFE_INTEGER,
        maxY: 0,
    };
}

export type MomentsVectorSpace = Readonly<{
    minorLength: number;
    majorLength: number;
    eccentricity: number;
    centerX: number;
    centerY: number;
    pointCount: number;
    theta: number | undefined;
}>;

export function getVectorSpaceForMoments(m: ImageMoments): MomentsVectorSpace {
    const { raw00 } = m;

    if (raw00 === 0) {
        return {
            minorLength: 0,
            majorLength: 0,
            centerX: 0,
            centerY: 0,
            pointCount: 0,
            eccentricity: 1,
            theta: undefined,
        };
    }

    const xAvg = m.rawX0 / raw00;
    const yAvg = m.raw0Y / raw00;

    const meanXY = m.rawXY / raw00 - xAvg * yAvg;
    const meanXX = m.rawXX / raw00 - xAvg * xAvg;
    const meanYY = m.rawYY / raw00 - yAvg * yAvg;

    const numerator = 2 * meanXY;
    const denominator = meanYY - meanXX;

    const avgMeans = (meanXX + meanYY) / 2;
    const meansRoot = Math.sqrt(numerator * numerator + denominator * denominator) / 2;

    const eigen1 = avgMeans + meansRoot;
    const eigen2 = avgMeans - meansRoot;
    const ratio = denominator / numerator;

    const arctan = 0.5 * Math.atan(ratio);
    let theta: number;

    if (isNaN(arctan)) {
        theta = 0;
    } else {
        const tmp = arctan + Math.PI + (meanXY >= 0 ? PI_4 : -PI_4);
        theta = tmp > Math.PI ? tmp - Math.PI : tmp;
    }

    const eccentricity = Math.sqrt(1 - eigen2 / eigen1);
    const minorLength = Math.sqrt(eccentricity < 1 ? eigen2 : 1) * 2;
    const majorLength = Math.sqrt(eigen1) * 2;

    return {
        minorLength,
        majorLength,
        eccentricity,
        centerX: m.rawX0 / m.raw00,
        centerY: m.raw0Y / m.raw00,
        pointCount: m.raw00,
        theta,
    };
}

export function getMomentsCentroid(m: ImageMoments): [number, number] {
    return [m.rawX0 / m.raw00, m.raw0Y / m.raw00];
}
